import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CLUB_ROLE_LABELS } from '@/entities/Club';
import { useGetBillingTariffsQuery, useUpdateBillingTariffsMutation } from '@/entities/BillingTariff';
import type { BillingTariffs, EditableBillingTariffRole, EditableBillingTariffs } from '@/entities/BillingTariff';
import { Button } from '@/shared/ui/Button/Button';
import { FormError } from '@/shared/ui/FormError/FormError';
import { Input } from '@/shared/ui/Input/Input';
import { Loader } from '@/shared/ui/Loader/Loader';
import { PageTitle } from '@/shared/ui/PageTitle/PageTitle';
import { Section } from '@/shared/ui/Section/Section';
import s from './BillingTariffsPage.module.scss';

const editableRoles: EditableBillingTariffRole[] = ['admin', 'player', 'observer', 'viewer', 'training', 'referee'];

const roleLabels: Record<keyof BillingTariffs, string> = {
  ...CLUB_ROLE_LABELS,
  training: 'Тренировочный модуль',
};

const getErrorMessage = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  if ('data' in error && typeof error.data === 'object' && error.data !== null && 'message' in error.data) {
    const message = error.data.message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if ('message' in error && typeof error.message === 'string' && error.message.trim()) return error.message;
  return null;
};

const isTariffValueValid = (value: string): boolean => {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value)) return false;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 && amount <= 9999999999.99;
};

const toDraft = (tariffs: BillingTariffs): Record<EditableBillingTariffRole, string> =>
  Object.fromEntries(editableRoles.map((role) => [role, String(tariffs[role])])) as Record<
    EditableBillingTariffRole,
    string
  >;

export const BillingTariffsPage = () => {
  const { data, error, isLoading, isFetching, refetch } = useGetBillingTariffsQuery();
  const [updateTariffs, updateState] = useUpdateBillingTariffsMutation();
  const [draft, setDraft] = useState<Record<EditableBillingTariffRole, string> | null>(null);
  const [baseline, setBaseline] = useState<BillingTariffs | null>(null);
  const draftRef = useRef<Record<EditableBillingTariffRole, string> | null>(null);
  const baselineRef = useRef<BillingTariffs | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!data) return;

    const previousBaseline = baselineRef.current;
    const currentDraft = draftRef.current;
    const nextDraft =
      !previousBaseline || !currentDraft
        ? toDraft(data.tariffs)
        : editableRoles.reduce<Record<EditableBillingTariffRole, string>>(
            (next, role) => {
              next[role] =
                currentDraft[role] === String(previousBaseline[role]) ? String(data.tariffs[role]) : currentDraft[role];
              return next;
            },
            {} as Record<EditableBillingTariffRole, string>,
          );

    baselineRef.current = data.tariffs;
    draftRef.current = nextDraft;
    setBaseline(data.tariffs);
    setDraft(nextDraft);
  }, [data]);

  const changedTariffs = useMemo(() => {
    if (!baseline || !draft) return {} as EditableBillingTariffs;
    return editableRoles.reduce<EditableBillingTariffs>((changed, role) => {
      const input = draft[role];
      if (isTariffValueValid(input) && Number(input) !== baseline[role]) changed[role] = Number(input);
      return changed;
    }, {});
  }, [baseline, draft]);

  const invalidRole = draft ? editableRoles.find((role) => !isTariffValueValid(draft[role])) : undefined;
  const canSave = !!draft && !invalidRole && Object.keys(changedTariffs).length > 0 && !updateState.isLoading;

  const handleChange = (role: EditableBillingTariffRole, event: ChangeEvent<HTMLInputElement> | string) => {
    const value = typeof event === 'string' ? event : event.target.value;
    const current = draftRef.current;
    if (current) {
      const nextDraft = { ...current, [role]: value };
      draftRef.current = nextDraft;
      setDraft(nextDraft);
    }
    setValidationError(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    const submittedDraft = draftRef.current;
    const currentBaseline = baselineRef.current;
    if (!submittedDraft || !currentBaseline || updateState.isLoading) return;
    const invalid = editableRoles.find((role) => !isTariffValueValid(submittedDraft[role]));
    if (invalid) {
      setValidationError(`Укажите корректную ставку для поля «${roleLabels[invalid]}».`);
      return;
    }

    const submittedTariffs = editableRoles.reduce<EditableBillingTariffs>((changed, role) => {
      const input = submittedDraft[role];
      if (Number(input) !== currentBaseline[role]) changed[role] = Number(input);
      return changed;
    }, {});
    if (!Object.keys(submittedTariffs).length) return;

    setSaveError(null);
    setSaveSuccess(false);
    try {
      const response = await updateTariffs(submittedTariffs).unwrap();
      const currentDraft = draftRef.current ?? submittedDraft;
      const nextDraft = editableRoles.reduce<Record<EditableBillingTariffRole, string>>(
        (next, role) => {
          next[role] =
            currentDraft[role] === submittedDraft[role] ? String(response.tariffs[role]) : currentDraft[role];
          return next;
        },
        {} as Record<EditableBillingTariffRole, string>,
      );
      baselineRef.current = response.tariffs;
      draftRef.current = nextDraft;
      setBaseline(response.tariffs);
      setDraft(nextDraft);
      setSaveSuccess(
        editableRoles.every(
          (role) => isTariffValueValid(nextDraft[role]) && Number(nextDraft[role]) === response.tariffs[role],
        ),
      );
    } catch (requestError) {
      setSaveError(getErrorMessage(requestError));
    }
  };

  const loadErrorMessage = getErrorMessage(error);

  return (
    <div className={s.container}>
      <PageTitle title="Тарифы" />
      <h1 className={s.pageTitle}>Настройки биллинга</h1>
      <Section title="Стоимость минуты">
        {isLoading && !data ? (
          <div className={s.loader}>
            <Loader />
          </div>
        ) : !data ? (
          <div className={s.errorState}>
            <FormError error={loadErrorMessage ?? ''} />
            <Button size="s" width="auto" variant="outline" onClick={() => void refetch()} disabled={isFetching}>
              Повторить
            </Button>
          </div>
        ) : (
          <div className={s.tariffs}>
            {loadErrorMessage && (
              <div className={s.loadError}>
                <FormError error={loadErrorMessage} />
                <Button size="s" width="auto" variant="outline" onClick={() => void refetch()} disabled={isFetching}>
                  Повторить загрузку
                </Button>
              </div>
            )}
            {editableRoles.map((role) => (
              <Input
                key={role}
                label={roleLabels[role]}
                value={draft?.[role] ?? ''}
                onChange={(event) => handleChange(role, event)}
                error={
                  draft && !isTariffValueValid(draft[role])
                    ? 'Введите число от 0 до 9999999999.99, максимум 2 знака после запятой.'
                    : ''
                }
                type="text"
                inputMode="decimal"
              />
            ))}
            <div className={s.readOnlyTariff}>
              <span>{roleLabels.developer}</span>
              <strong>Бесплатно</strong>
              <span>0 технокоина за минуту</span>
            </div>
            <div className={s.actions}>
              <Button size="s" width="auto" onClick={() => void handleSave()} disabled={!canSave}>
                {updateState.isLoading ? 'Сохранение…' : 'Сохранить'}
              </Button>
              {validationError && <FormError error={validationError} />}
              {saveError && <FormError error={saveError} />}
              {saveSuccess && <span className={s.success}>Тарифы сохранены</span>}
            </div>
            <p className={s.hint}>Ставка указывается в технокоинах за минуту.</p>
          </div>
        )}
      </Section>
    </div>
  );
};

export default BillingTariffsPage;
